import { Button, ButtonGroup } from '@mui/material';
import { useEffect, useState } from 'react';
import Geocode from 'react-geocode';

import PublicIcon from '@mui/icons-material/Public';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import NearMeIcon from '@mui/icons-material/NearMe';

import { LatLng } from '..';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState, store } from '../../../store';
import { GOOGLE_API_KEY } from '../../../constants/google-map.constants';

// ZOOM_LEVEL_CITY is the zoom level for viewing a city
const ZOOM_LEVEL_CITY = 12;

// ZOOM_LEVEL_AMERICA is the zoom level for viewing the whole country of America
const ZOOM_LEVEL_AMERICA = 4;

export const defaultCoordinates = {
    center: {
        lat: 35.99835602,
        lng: -90.01502627,
    },
    zoom: ZOOM_LEVEL_AMERICA,
};

const AMERICA_CENTER = defaultCoordinates.center;

type Props = {
    setCenter: React.Dispatch<React.SetStateAction<LatLng>>;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
};

export function EventMapButtonBar({ setZoom, setCenter }: Props) {
    const { authUser } = useSelector(
        (state: RootState) => state.auth,
        (prev, next) =>
            prev?.authUser?.homeLocation?.city ===
            next?.authUser?.homeLocation?.city
    );

    const localPosition = useSelector(
        (state: RootState) => {
            if (state?.customer?.details?.customer?.localPosition) {
                return state?.customer?.details?.customer?.localPosition;
            } else {
                return defaultCoordinates.center;
            }
        },
        (prev, next) => prev.lat === next.lat
    );

    const [isLocalButtonDisabled, setIsLocalButtonDisabled] = useState(
        localPosition.lat === defaultCoordinates.center.lat &&
            localPosition.lng === defaultCoordinates.center.lng
    );

    // workPosition is the LatLng coordinates
    const workPosition = useSelector(
        (state: RootState) => {
            if (state?.customer?.details?.customer?.workPosition) {
                return state?.customer?.details.customer?.workPosition;
            } else {
                return defaultCoordinates.center;
            }
        },
        (prev, next) => prev.lat === next.lat
    );

    const homePosition = useSelector(
        (state: RootState) => {
            if (state?.customer?.details?.customer?.homePosition) {
                return state?.customer?.details.customer?.homePosition;
            } else {
                return defaultCoordinates.center;
            }
        },
        (prev, next) => prev.lat === next.lat
    );

    // useEffect to on first component render, get the location of the browser
    useEffect(() => {
        // get location of browser with geolocation, setLocationPosition state
        navigator.geolocation.getCurrentPosition((position) => {
            const localPosition = {
                lat: position?.coords?.latitude,
                lng: position?.coords?.longitude,
            };

            setIsLocalButtonDisabled(
                () => localPosition === defaultCoordinates.center
            );

            store.dispatch.customer.setLocalPosition(localPosition);
        });
    }, [localPosition]);

    // do google geolocate on this
    Geocode.setApiKey(GOOGLE_API_KEY);

    // setWorkPosition puts the customer's work GPS coordinates into the store
    async function setWorkPosition(workLocation: WorkLocationProps) {
        // Validate location data exists
        if (!workLocation || !isValidLocation(workLocation)) {
            console.warn('Work location data is invalid or incomplete:', workLocation);
            return;
        }

        try {
            const workLocationString = makeLocationString(workLocation);
            const geocodeCall = await Geocode.fromAddress(workLocationString);
            
            // Check if geocoding was successful and has results
            if (geocodeCall.results && geocodeCall.results.length > 0) {
                const workPosition = geocodeCall.results[0].geometry.location;
                store.dispatch.customer.setWorkPosition(workPosition);
            } else {
                console.warn('No geocoding results found for work location:', workLocationString);
            }
        } catch (error) {
            console.error('Error geocoding work location:', error);
            // Don't show toast error to user for this background operation
        }
    }

    async function setHomePosition(homeLocation: HomeLocationProps) {
        // Validate location data exists
        if (!homeLocation || !isValidLocation(homeLocation)) {
            console.warn('Home location data is invalid or incomplete:', homeLocation);
            return;
        }

        try {
            const homeLocationString = makeLocationString(homeLocation);
            const geocodeCall = await Geocode.fromAddress(homeLocationString);
            
            // Check if geocoding was successful and has results
            if (geocodeCall.results && geocodeCall.results.length > 0) {
                const homePosition = geocodeCall.results[0].geometry.location;
                store.dispatch.customer.setHomePosition(homePosition);
            } else {
                console.warn('No geocoding results found for home location:', homeLocationString);
            }
        } catch (error) {
            console.error('Error geocoding home location:', error);
            // Don't show toast error to user for this background operation
        }
    }

    useEffect(() => {
        const homeLocation = authUser?.homeLocation;
        const workLocation = authUser?.workLocation;
        setHomePosition(homeLocation);
        setWorkPosition(workLocation);
    }, [workPosition?.lng, homePosition?.lat]);

    // setIsLocalButtonDisabled to true if the local position is the default

    return (
        <ButtonGroup
            variant="contained"
            aria-label="outlined primary button group"
            sx={{ marginRight: 20 }}
            size="small"
        >
            <Button onClick={() => setGlobal({ setZoom, setCenter })}>
                <PublicIcon />
            </Button>

            <Button onClick={() => setWork(setZoom, setCenter, workPosition)}>
                <BusinessIcon />
            </Button>
            <Button onClick={() => setHome(setZoom, setCenter, homePosition)}>
                <HomeIcon />
            </Button>
            <Button
                disabled={isLocalButtonDisabled}
                onClick={() => setLocal(setZoom, setCenter, localPosition)}
            >
                <NearMeIcon />
            </Button>
        </ButtonGroup>
    );
}

// set local positions the map around the user
function setLocal(
    setZoom: React.Dispatch<React.SetStateAction<number>>,
    setCenter: React.Dispatch<React.SetStateAction<LatLng>>,
    localPosition: LatLng
) {
    if (
        localPosition.lat === defaultCoordinates.center.lat &&
        localPosition.lng === defaultCoordinates.center.lng
    ) {
        toast(
            'Still loading your local location from browser, please wait...',
            { autoClose: 1000, type: 'info' }
        );

        return;
    }
    setCenter(localPosition);

    // zoom in after the new center is created
    setZoom(ZOOM_LEVEL_CITY);
}

// set global map position to view all defices. Early version of this just shows America
function setGlobal({ setZoom, setCenter }: Props) {
    setCenter(AMERICA_CENTER);
    setZoom(ZOOM_LEVEL_AMERICA);
}

function setWork(
    setZoom: React.Dispatch<React.SetStateAction<number>>,
    setCenter: React.Dispatch<React.SetStateAction<LatLng>>,
    workPosition: LatLng
) {
    if (
        workPosition.lat === defaultCoordinates.center.lat &&
        workPosition.lng === defaultCoordinates.center.lng
    ) {
        toast('Still loading your work location, please wait...', {
            autoClose: 1000,
            type: 'info',
        });

        return;
    }

    setCenter(workPosition);
    setZoom(ZOOM_LEVEL_CITY);
}

function setHome(
    setZoom: React.Dispatch<React.SetStateAction<number>>,
    setCenter: React.Dispatch<React.SetStateAction<LatLng>>,
    homePosition: LatLng
) {
    if (
        homePosition.lat === defaultCoordinates.center.lat &&
        homePosition.lng === defaultCoordinates.center.lng
    ) {
        toast('Still loading your home location, please wait...', {
            autoClose: 1000,
            type: 'info',
        });

        return;
    }

    setCenter(homePosition);
    setZoom(ZOOM_LEVEL_CITY);
}

// HomeLocationProps are for setHomePosition, which takes a location and geolocates GPS coordinates
type HomeLocationProps = {
    city?: string;
    zip?: string;
    state?: string;
    address?: string;
};

// WorkLocationProps are for setWorkPosition, which takes a location and geolocates GPS coordinates
type WorkLocationProps = {
    city?: string;
    zip?: string;
    state?: string;
    address?: string;
};

// Location is any work or home location
type Location = WorkLocationProps & HomeLocationProps;

// Helper function to validate if location has enough data for geocoding
function isValidLocation(location: Location): boolean {
    return !!(location && (location.city || location.address) && location.state);
}

function makeLocationString(location: Location) {
    // Filter out undefined/null values and join with spaces
    const parts = [location?.address, location?.city, location?.state, location?.zip]
        .filter(part => part && part.trim() !== '');
    
    return parts.join(' ');
}
